/**
 * NFTPicker - Modal for selecting NFTs from a wallet
 *
 * Shows a searchable grid of NFTs owned by a given wallet address.
 * NFTs in active bundles or trades are shown grayed out.
 */

import React, { useState, useEffect } from 'react';
import { FeatherX, FeatherSearch } from '@subframe/core';
import { apiClient, ApiResponse } from '../../api/client';
import { is3DModelUrl } from '../../utils/media';

interface NFTForPicker {
  nft_token_id: string;
  name?: string | null;
  image_url?: string | null;
  thumbnail_url?: string | null;
  rarity_rank?: number | null;
  collection_name?: string | null;
  collection_id?: string | null;
  floor_price_drops?: string | null;
  in_bundle?: boolean;
  in_trade?: boolean;
}

interface NFTPickerProps {
  walletAddress: string;
  onSelect: (nft: NFTForPicker) => void;
  onClose: () => void;
  excludeTokenIds?: string[];
}

// Response type matches /wallets/:address endpoint
interface WalletResponse {
  wallet: {
    address: string;
    [key: string]: unknown;
  };
  nfts: {
    total: number;
    collections: Array<{
      collectionId: string;
      collectionName: string;
      collectionImage: string | null;
      nftCount: number;
      nfts: Array<{
        nftTokenId: string;
        name: string | null;
        image: string | null;
        collectionId: string;
        rarity_rank?: number | null;
      }>;
    }>;
  };
}

export default function NFTPicker({
  walletAddress,
  onSelect,
  onClose,
  excludeTokenIds = [],
}: NFTPickerProps) {
  const [nfts, setNfts] = useState<NFTForPicker[]>([]);
  const [filteredNfts, setFilteredNfts] = useState<NFTForPicker[]>([]);
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadNfts() {
      setIsLoading(true);
      setError(null);

      try {
        // Use the main wallet endpoint which includes NFTs grouped by collection
        const response = await apiClient.get<ApiResponse<WalletResponse>>(
          `/wallets/${walletAddress}`
        );

        if (response.success && response.data?.nfts?.collections) {
          // Flatten all collections' NFTs into a single list
          const allNfts: NFTForPicker[] = [];

          for (const collection of response.data.nfts.collections) {
            for (const nft of collection.nfts) {
              allNfts.push({
                nft_token_id: nft.nftTokenId,
                name: nft.name,
                image_url: nft.image,
                thumbnail_url: nft.image,
                rarity_rank: nft.rarity_rank,
                collection_name: collection.collectionName,
                collection_id: collection.collectionId,
              });
            }
          }

          setNfts(allNfts);
          setFilteredNfts(allNfts);
        } else {
          setError('Failed to load NFTs');
        }
      } catch (err) {
        console.error('Failed to load NFTs:', err);
        setError('Failed to load NFTs');
      } finally {
        setIsLoading(false);
      }
    }

    loadNfts();
  }, [walletAddress]);

  useEffect(() => {
    if (!search.trim()) {
      setFilteredNfts(nfts);
      return;
    }
    const q = search.toLowerCase();
    setFilteredNfts(
      nfts.filter(
        (n) =>
          (n.name && n.name.toLowerCase().includes(q)) ||
          (n.collection_name && n.collection_name.toLowerCase().includes(q)) ||
          n.nft_token_id.toLowerCase().includes(q)
      )
    );
  }, [search, nfts]);

  const excludeSet = new Set(excludeTokenIds);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
      <div className="bg-default-background rounded-md border border-solid border-neutral-border shadow-lg w-full max-w-lg max-h-[80vh] flex flex-col mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-solid border-neutral-border">
          <h3 className="text-body-bold font-body-bold text-default-font">Select NFT</h3>
          <button onClick={onClose} className="p-1 hover:bg-neutral-100 rounded-md">
            <FeatherX className="h-5 w-5 text-subtext-color" />
          </button>
        </div>

        {/* Search */}
        <div className="px-4 pt-3 pb-2">
          <div className="relative">
            <FeatherSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-subtext-color" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or collection..."
              className="w-full pl-9 pr-3 py-2 border border-solid border-neutral-border rounded-md text-sm text-default-font bg-default-background focus:outline-none focus:ring-2 focus:ring-brand-primary"
              autoFocus
            />
          </div>
        </div>

        {/* NFT Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-primary"></div>
              <span className="ml-2 text-body font-body text-subtext-color">Loading NFTs...</span>
            </div>
          )}

          {error && (
            <div className="text-center py-8 text-sm text-error-700">{error}</div>
          )}

          {!isLoading && !error && filteredNfts.length === 0 && (
            <div className="text-center py-8 text-body font-body text-subtext-color">
              {search ? 'No NFTs match your search' : 'No NFTs found in this wallet'}
            </div>
          )}

          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {filteredNfts.map((nft) => {
              const isExcluded = excludeSet.has(nft.nft_token_id);
              const isDisabled = isExcluded || nft.in_bundle || nft.in_trade;

              return (
                <button
                  key={nft.nft_token_id}
                  onClick={() => !isDisabled && onSelect(nft)}
                  disabled={isDisabled}
                  className={`relative rounded-md overflow-hidden border border-solid transition-colors text-left ${
                    isDisabled
                      ? 'opacity-40 cursor-not-allowed border-neutral-border'
                      : 'border-neutral-border hover:border-brand-primary cursor-pointer'
                  }`}
                >
                  <div className="aspect-square bg-neutral-100">
                    {is3DModelUrl(nft.thumbnail_url || nft.image_url) ? (
                      <div className="w-full h-full bg-gradient-to-br from-neutral-100 to-neutral-200 flex flex-col items-center justify-center gap-0.5">
                        <span className="text-lg">🧊</span>
                        <span className="text-[10px] text-neutral-500">3D</span>
                      </div>
                    ) : (nft.thumbnail_url || nft.image_url) ? (
                      <img
                        src={nft.thumbnail_url || nft.image_url || ''}
                        alt={nft.name || 'NFT'}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xs text-subtext-color">
                        NFT
                      </div>
                    )}
                  </div>
                  <div className="p-1.5">
                    <div className="text-xs font-medium text-default-font truncate">
                      {nft.name || 'Unnamed'}
                    </div>
                    {nft.collection_name && (
                      <div className="text-xs text-subtext-color truncate">
                        {nft.collection_name}
                      </div>
                    )}
                  </div>
                  {nft.in_bundle && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <span className="text-xs text-white font-medium bg-black/50 px-2 py-1 rounded">
                        In Bundle
                      </span>
                    </div>
                  )}
                  {nft.in_trade && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <span className="text-xs text-white font-medium bg-black/50 px-2 py-1 rounded">
                        In Trade
                      </span>
                    </div>
                  )}
                  {isExcluded && !nft.in_bundle && !nft.in_trade && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <span className="text-xs text-white font-medium bg-black/50 px-2 py-1 rounded">
                        Already Added
                      </span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
